// frontend/src/components/admin/DuplicationClientModal.jsx
// ============================================================================
// DUPLIQUER UN CLIENT
// ----------------------------------------------------------------------------
// Creer un client a l'image d'un autre : son offre, ses plafonds, ses
// extensions, son parametrage de depart. Jamais ses donnees.
//
// Le mot de passe provisoire de l'administrateur n'apparait qu'une fois, a la
// fin, dans l'ecran de resultat. Il n'est ecrit dans aucun journal.
// ============================================================================

import React, { useState, useMemo, useEffect } from 'react';
import {
  Copy, X, Check, Building2, Shield, Zap, Settings2, UserCheck,
  AlertTriangle, Loader2, CheckCircle2
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

/** « LCC GROUP » -> « lcc-group » */
const enSlug = (valeur) =>
  String(valeur || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

/** admin@ancien.com + nouveau slug -> admin@nouveau.com */
const transposerAdresse = (adresse, slug) => {
  const source = String(adresse || '');
  const arobase = source.indexOf('@');
  if (arobase < 1 || !slug) return '';
  const point = source.lastIndexOf('.');
  const extension = point > arobase ? source.slice(point) : '.com';
  return `${source.slice(0, arobase)}@${slug}${extension}`;
};

const Champ = ({ libelle, obligatoire, aide, children, t }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: t.textMuted }}>
      {libelle}{obligatoire && <span style={{ color: t.dangerText }}> *</span>}
    </span>
    {children}
    {aide && <span style={{ fontSize: 11, color: t.textMuted }}>{aide}</span>}
  </label>
);

const Bascule = ({ actif, onChange, titre, detail, Icone, t }) => (
  <button
    type="button"
    onClick={() => onChange(!actif)}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 11, textAlign: 'left', width: '100%',
      background: actif ? t.primarySoft : t.bgInput,
      border: `1px solid ${actif ? t.primaryBorder : t.border}`,
      borderRadius: 12, padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit'
    }}
  >
    <span style={{
      width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
      background: actif ? t.primary : 'transparent',
      border: `1px solid ${actif ? t.primary : t.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      {actif && <Check size={12} style={{ color: '#fff' }} />}
    </span>
    <span style={{ flexGrow: 1 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600, color: t.text }}>
        <Icone size={14} style={{ color: actif ? t.primaryText : t.textMuted }} />
        {titre}
      </span>
      <span style={{ display: 'block', fontSize: 12, color: t.textMuted, marginTop: 3, lineHeight: 1.5 }}>
        {detail}
      </span>
    </span>
  </button>
);

const DuplicationClientModal = ({ modele, onClose, onSuccess }) => {
  const t = useTheme();

  const [nom, setNom] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouche, setSlugTouche] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactTelephone, setContactTelephone] = useState('');
  const [interlocuteur, setInterlocuteur] = useState('');
  const [ville, setVille] = useState('');

  const [copierParametrage, setCopierParametrage] = useState(true);
  const [copierAdmin, setCopierAdmin] = useState(true);
  const [adminNom, setAdminNom] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminTouche, setAdminTouche] = useState(false);

  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [copie, setCopie] = useState(false);

  const slugPropose = useMemo(() => (slugTouche ? slug : enSlug(nom)), [nom, slug, slugTouche]);

  const [adminModele, setAdminModele] = useState(null);
  const [lectureAdmin, setLectureAdmin] = useState(true);

  useEffect(() => {
    let vivant = true;
    superAdminApi.companies.getUsers(modele.id)
      .then((r) => {
        if (!vivant) return;
        const comptes = r.data || [];
        const principal = comptes.find((c) => c.role === 'admin' && c.is_primary_admin)
          || comptes.find((c) => c.role === 'admin')
          || null;
        setAdminModele(principal);
        setCopierAdmin(Boolean(principal));
      })
      .catch(() => { if (vivant) setAdminModele(null); })
      .finally(() => { if (vivant) setLectureAdmin(false); });
    return () => { vivant = false; };
  }, [modele.id]);
  const adresseProposee = useMemo(() => {
    if (adminTouche) return adminEmail;
    return transposerAdresse(adminModele?.email, slugPropose);
  }, [adminModele, slugPropose, adminEmail, adminTouche]);

  const valider = async () => {
    setErreur(null);

    if (!nom.trim()) return setErreur('Le nom du nouveau client est obligatoire');
    if (!slugPropose) return setErreur('Le slug est obligatoire');
    if (copierAdmin && !adresseProposee.trim()) {
      return setErreur("L'adresse de l'administrateur est obligatoire");
    }

    setEnvoi(true);
    try {
      const reponse = await superAdminApi.companies.dupliquer(modele.id, {
        company_name: nom.trim(),
        company_slug: slugPropose,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactTelephone.trim() || null,
        interlocuteur: interlocuteur.trim() || null,
        city: ville.trim() || null,
        copier_parametrage: copierParametrage,
        admin: copierAdmin
          ? { nom: adminNom.trim() || adminModele?.name || 'Administrateur', email: adresseProposee.trim() }
          : null
      });
      setResultat(reponse.data);
    } catch (e) {
      setErreur(e.message || 'La duplication a échoué');
    } finally {
      setEnvoi(false);
    }
  };

  const fermer = () => {
    if (resultat) onSuccess?.();
    onClose();
  };

  const champStyle = {
    background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
    padding: '9px 12px', fontSize: 13, color: t.text, outline: 'none', fontFamily: 'inherit'
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: t.bgOverlay, zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !envoi) fermer(); }}
    >
      <div style={{
        background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16,
        width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxShadow: t.shadowLg
      }}>

        {/* En-tête */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${t.borderLight}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: t.text,
              display: 'flex', alignItems: 'center', gap: 9 }}>
              <Copy size={17} style={{ color: t.primary }} />
              {resultat ? 'Client créé' : 'Dupliquer un client'}
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: t.textMuted }}>
              {resultat
                ? `${resultat.company_name} a été créé à l'image de ${resultat.modele.company_name}`
                : `À l'image de ${modele?.company_name} — son offre, ses extensions, son paramétrage. Aucune de ses données.`}
            </p>
          </div>
          <button type="button" onClick={fermer} disabled={envoi}
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: t.textMuted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Formulaire */}
        {!resultat && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {erreur && (
              <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 10,
                padding: '11px 14px', fontSize: 13, color: t.dangerText,
                display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={15} /> {erreur}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <Champ libelle="Nom du nouveau client" obligatoire t={t}>
                <input value={nom} onChange={(e) => setNom(e.target.value)}
                  placeholder="Nouvelle entreprise" style={champStyle} />
              </Champ>
              <Champ libelle="Slug" obligatoire aide="Identifiant court, en minuscules" t={t}>
                <input
                  value={slugPropose}
                  onChange={(e) => { setSlugTouche(true); setSlug(enSlug(e.target.value)); }}
                  placeholder="nouvelle-entreprise" style={champStyle} />
              </Champ>
              <Champ libelle="E-mail de contact" t={t}>
                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@entreprise.com" style={champStyle} />
              </Champ>
              <Champ libelle="Téléphone" t={t}>
                <input value={contactTelephone} onChange={(e) => setContactTelephone(e.target.value)}
                  placeholder="+242 ..." style={champStyle} />
              </Champ>
              <Champ libelle="Interlocuteur" t={t}>
                <input value={interlocuteur} onChange={(e) => setInterlocuteur(e.target.value)}
                  placeholder="Nom du responsable" style={champStyle} />
              </Champ>
              <Champ libelle="Ville" t={t}>
                <input value={ville} onChange={(e) => setVille(e.target.value)}
                  placeholder="Brazzaville" style={champStyle} />
              </Champ>
            </div>

            {/* Ce qui est repris, sans choix : c'est le sens même de l'action */}
            <div style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 12, padding: '12px 14px' }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: t.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.5px' }}>Repris automatiquement</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: t.textSecondary }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Shield size={13} style={{ color: t.primaryText }} />
                  L&rsquo;offre et la licence : type, plafonds, extensions payantes, modules — numéro et dates neufs, même durée
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Zap size={13} style={{ color: t.primaryText }} />
                  Les extensions activées, dérogations et prix compris
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Building2 size={13} style={{ color: t.primaryText }} />
                  Le cadre commercial : jour de facturation, remise, fuseau, couleur — et un essai de 30 jours qui démarre aujourd&rsquo;hui
                </span>
              </div>
            </div>

            <Bascule
              actif={copierParametrage} onChange={setCopierParametrage} Icone={Settings2} t={t}
              titre="Reprendre le paramétrage de départ"
              detail="Départements, types de contrat, groupes salariaux, paliers de retard, règles de pointage, paramètres comptables, plan comptable, catégories de mouvement, entrepôts. Les compteurs de documents repartent à 1."
            />

            <Bascule
              actif={copierAdmin} onChange={setCopierAdmin} Icone={UserCheck} t={t}
              titre="Créer l'administrateur principal"
              detail={lectureAdmin
                ? 'Lecture des comptes du client modèle…'
                : adminModele
                  ? `Reprend le nom, le rôle et les droits de ${adminModele.name || adminModele.email}. Nouvelle adresse et mot de passe provisoire montré une seule fois.`
                  : "Le client modèle n'a pas d'administrateur : rien à reprendre."}
            />

            {copierAdmin && adminModele && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12,
                paddingLeft: 14, borderLeft: `2px solid ${t.primaryBorder}` }}>
                <Champ libelle="Nom de l'administrateur" t={t}>
                  <input value={adminNom} onChange={(e) => setAdminNom(e.target.value)}
                    placeholder={adminModele.name || 'Administrateur'} style={champStyle} />
                </Champ>
                <Champ libelle="Adresse e-mail" obligatoire aide="Proposée d'après celle du modèle" t={t}>
                  <input type="email" value={adresseProposee}
                    onChange={(e) => { setAdminTouche(true); setAdminEmail(e.target.value); }}
                    placeholder="admin@entreprise.com" style={champStyle} />
                </Champ>
              </div>
            )}

            <p style={{ margin: 0, fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>
              Aucune donnée du client modèle n&rsquo;est copiée : ni employés, ni pointages, ni paie,
              ni caisse, ni comptabilité, ni documents. Si quoi que ce soit échoue, rien n&rsquo;est créé.
            </p>
          </div>
        )}

        {/* Résultat */}
        {resultat && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: t.successSoft, border: `1px solid ${t.success}40`, borderRadius: 12,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
                fontWeight: 700, color: t.successText }}>
                <CheckCircle2 size={16} /> {resultat.company_name}
              </span>
              <div style={{ fontSize: 13, color: t.textSecondary, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>Slug : {resultat.company_slug} · code {resultat.code}</span>
                {resultat.licence && (
                  <span>
                    Licence {resultat.licence.type_licence} — {resultat.licence.numero_licence},
                    {' '}{resultat.licence.jours} jours comme le modèle
                  </span>
                )}
                <span>{resultat.extensions} extension(s) reprises</span>
                {Object.keys(resultat.parametrage || {}).length > 0 && (
                  <span>
                    Paramétrage :{' '}
                    {Object.entries(resultat.parametrage)
                      .map(([table, n]) => `${n} ${table.replace(/_/g, ' ')}`)
                      .join(', ')}
                  </span>
                )}
                <span>Essai jusqu&rsquo;au {resultat.essai_jusqu_au}</span>
              </div>
            </div>

            {resultat.admin && (
              <div style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 12,
                padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
                  Administrateur : {resultat.admin.email}
                </span>
                <span style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>
                  Mot de passe provisoire, à remettre en main propre. Il ne sera plus jamais
                  affiché et n&rsquo;est écrit dans aucun journal. Le compte devra le changer à
                  sa première connexion.
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <code style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.04em', color: t.text,
                    background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
                    padding: '10px 14px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {resultat.admin.provisoire}
                  </code>
                  <button type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(resultat.admin.provisoire);
                      setCopie(true);
                      setTimeout(() => setCopie(false), 2000);
                    }}
                    style={{ ...champStyle, cursor: 'pointer', display: 'inline-flex',
                      alignItems: 'center', gap: 7, fontWeight: 600 }}>
                    {copie ? <Check size={14} /> : <Copy size={14} />}
                    {copie ? 'Copié' : 'Copier'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pied */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${t.borderLight}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {!resultat ? (
            <>
              <button type="button" onClick={fermer} disabled={envoi}
                style={{ ...champStyle, cursor: 'pointer', fontWeight: 600, minWidth: 110 }}>
                Annuler
              </button>
              <button type="button" onClick={valider} disabled={envoi}
                style={{ background: t.primary, border: `1px solid ${t.primary}`, borderRadius: 10,
                  padding: '9px 18px', fontSize: 13, fontWeight: 700, color: '#fff',
                  cursor: envoi ? 'default' : 'pointer', fontFamily: 'inherit', minWidth: 150,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {envoi ? <Loader2 size={14} className="dup-tourne" /> : <Copy size={14} />}
                {envoi ? 'Duplication…' : 'Dupliquer le client'}
              </button>
            </>
          ) : (
            <button type="button" onClick={fermer}
              style={{ background: t.primary, border: `1px solid ${t.primary}`, borderRadius: 10,
                padding: '9px 18px', fontSize: 13, fontWeight: 700, color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit', minWidth: 150 }}>
              J&rsquo;ai noté, fermer
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes dupTourne { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        .dup-tourne { animation: dupTourne 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default DuplicationClientModal;
